import { QueryClient, focusManager } from '@tanstack/vue-query'
import { ref, computed, watchEffect, onUnmounted, readonly } from 'vue'
import all from '@/modules/integration/data/all'

// Create a client instance with better defaults to prevent refetching
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  }
})

focusManager.setEventListener((handleFocus) => {
  // Listen to visibilitychange
  if (typeof window !== 'undefined' && window.addEventListener) {
    const visibilitychangeHandler = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: ['integrations'] })
      }
    }
    window.addEventListener('visibilitychange', visibilitychangeHandler, false)
    return () => {
      // Be sure to unsubscribe if a new handler is set
      window.removeEventListener('visibilitychange', visibilitychangeHandler)
    }
  }
})

export const getIntegrationData = async (scope: string, key: string) => {
  const { currentTeamPid } = useConfigStore()
  const { integratedPartner } = useIntegrationStore()
  const cacheKey = ['integrations', currentTeamPid, scope, key]

  if (scope === 'partner' && !integratedPartner?.name) {
    return computed(() => [])
  }

  // Check if data exists in cache first
  const cachedData = queryClient.getQueryData(cacheKey)
  if (cachedData !== undefined) {
    return computed(() => cachedData)
  }

  // Only fetch if not in cache
  const result = await queryClient.fetchQuery({
    queryKey: cacheKey,
    queryFn: () => all[scope][key].queryFn(currentTeamPid),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000 // Keep in cache for 30 minutes
  })

  // Return a reactive computed that always gets the latest data from the query cache
  const data = computed(() => {
    const currentData = queryClient.getQueryData(cacheKey)
    return currentData || result || []
  })

  return data
}

// New function to create a reactive integration data watcher
export const useIntegrationData = (scope: string, key: string) => {
  const configStore = useConfigStore()
  const { integratedPartner } = useIntegrationStore()

  // Create cache key that updates with currentTeamPid
  const cacheKey = computed(() => [
    'integrations',
    configStore.currentTeamPid,
    scope,
    key
  ])

  // Create a reactive reference that updates when query data changes
  const data = ref([])
  const isLoading = ref(true)

  const fetchData = async () => {
    if (scope === 'partner' && !integratedPartner?.name) {
      data.value = []
      isLoading.value = false
      return
    }

    // Check cache first before fetching
    const cachedData = queryClient.getQueryData(cacheKey.value)
    if (cachedData !== undefined) {
      data.value = cachedData
      isLoading.value = false
      return
    }

    try {
      isLoading.value = true
      const result = await queryClient.fetchQuery({
        queryKey: cacheKey.value,
        queryFn: () => all[scope][key].queryFn(configStore.currentTeamPid),
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        cacheTime: 30 * 60 * 1000 // Keep in cache for 30 minutes
      })
      data.value = result || []
    } catch (error) {
      console.error(
        `Error fetching integration data for ${scope}/${key}:`,
        error
      )
      data.value = []
    } finally {
      isLoading.value = false
    }
  }

  // Initial fetch
  fetchData()

  // Subscribe to query cache updates
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    const keyMatch =
      JSON.stringify(event.query.queryKey) === JSON.stringify(cacheKey.value)

    if (keyMatch && event.type === 'updated') {
      data.value = event.query.state.data || []
    } else if (keyMatch && event.type === 'removed') {
      fetchData()
    }
  })

  // Clean up subscription
  onUnmounted(() => {
    unsubscribe()
  })

  return {
    data: readonly(data),
    isLoading: readonly(isLoading),
    refetch: fetchData
  }
}

// Function to invalidate specific integration data queries
export const refreshIntegrationData = (scope: string, key: string) => {
  const { currentTeamPid } = useConfigStore()
  const cacheKey = ['integrations', currentTeamPid, scope, key]

  // Invalidate the specific query to force a refetch
  queryClient.invalidateQueries({ queryKey: cacheKey })

  // Also try to refetch immediately
  queryClient.refetchQueries({ queryKey: cacheKey })
}
